/**
 * @引用：生图 / 生视频节点提示词中的 @图N 标记（兼容 @图1 / @图 1 / @image1 / @img1）。
 * N 按**稳定图号**解析：图号刻在图片/图片组节点上（卡片角标 #N），创建时
 * 分配、终身不变、删除不回收——与其他节点的增删/连线顺序无关，杜绝位置
 * 编号的静默漂移。
 *
 * 语义（两层规则，不破坏现状）：
 *  - 提示词无标记：维持现行为（上游图片按连线全量自动注入参考图），返回 null；
 *  - 提示词含标记：切换为精确注入——本次只注入标记引用的上游资产
 *    （按标记出现顺序去重），标记替换为「参考图（N 张）」后随提示词
 *    发给模型，避免残缺句式；
 *  - 失效引用给明确提示（不静默）：图号节点已删除 / 还在但未连接到当前
 *    节点 / 图号不存在，均计入 warnings 由调用方 toast，标记替换为「参考图」。
 *
 * 解析统一放在 runImageGeneration / runVideoGeneration / resolveLlmInvocation 内
 * （节点按钮 / 运行链路 / 执行全部共用）；智能体暂不支持 @引用（按原样透传）。
 */
import type { UpstreamImageAssetGroup } from './state/upstream';

/** 含标记检测（非全局，避免 lastIndex 状态）；替换用全局正则在函数内新建 */
const MENTION_TEST = /@(?:图|image|img)\s*\d+/i;

export interface AtReferenceResolution {
  /** 替换掉 @图N 标记后的最终提示词 */
  prompt: string;
  /** 标记显式引用的参考图（按出现顺序去重；无有效引用时为空） */
  referenceImages: string[];
  /** 失效引用说明（节点已删 / 未连接 / 图号不存在） */
  warnings: string[];
}

export function resolveAtReferences(
  prompt: string,
  upstreamGroups: UpstreamImageAssetGroup[],
  /** 画布上现存的所有图号（用于区分"节点已删除"与"未连接到当前节点"） */
  knownRefNos?: Set<number>
): AtReferenceResolution | null {
  if (!prompt || !MENTION_TEST.test(prompt)) return null;

  const pattern = /@(?:图|image|img)\s*(\d+)/gi;
  const referenceImages: string[] = [];
  const warnings: string[] = [];

  const resolved = prompt.replace(pattern, (match, indexRaw: string | undefined) => {
    const refNo = indexRaw ? parseInt(indexRaw, 10) : 1;
    const group = upstreamGroups.find((g) => g.refNo === refNo);
    if (!group) {
      if (knownRefNos?.has(refNo)) {
        warnings.push(`${match}：图片 #${refNo} 未连接到当前节点，本次生成不包含它`);
      } else {
        warnings.push(`${match}：图片 #${refNo} 不存在或已被删除`);
      }
      return '参考图';
    }
    for (const url of group.urls) {
      if (!referenceImages.includes(url)) referenceImages.push(url);
    }
    return group.urls.length > 1 ? `参考图（${group.urls.length} 张）` : '参考图';
  });

  return { prompt: resolved, referenceImages, warnings };
}
